import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { getServices, getAvailableSlots, bookAppointment, getBarbers } from '../../services/api';
import type { Service, Barber } from '../../types';  // Import types
import TimeSlots from './TimeSlots';

interface AvailableSlot {
  date: string;
  time: string;
}

const BookAppointment: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [selectedBarber, setSelectedBarber] = useState<number | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);

  const fetchServices = async () => {
    try {
      const data = await getServices();
      setServices(data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchAvailableSlots = async () => {
    if (!selectedDate || !selectedBarber || !selectedService) return;
    try {
      const data = await getAvailableSlots(
        selectedBarber,
        selectedService.id,
        format(selectedDate, 'yyyy-MM-dd'),
        format(selectedDate, 'yyyy-MM-dd')  // For single day, use same date
      );
      setAvailableSlots(data);
    } catch (error) {
      console.error('Error fetching available slots:', error);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (selectedBarber && selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedBarber, selectedDate]);

  useEffect(() => {
    const fetchBarbers = async () => {
      try {
        const data = await getBarbers();
        setBarbers(data);
      } catch (error) {
        console.error('Error fetching barbers:', error);
      }
    };
    fetchBarbers();
  }, []);

  const handleSubmit = async () => {
    if (!selectedService || !selectedBarber || !selectedDate || !selectedTime) return;

    try {
      await bookAppointment({
        service_id: selectedService.id,
        barber_id: selectedBarber,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
      });
      // Reset form or show success message
    } catch (error) {
      console.error('Error booking appointment:', error);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Book an Appointment
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Service</InputLabel>
            <Select
              value={selectedService?.id || ''}
              label="Service"
              onChange={(e: SelectChangeEvent<number>) => {
                const service = services.find(s => s.id === e.target.value);
                setSelectedService(service || null);
              }}
            >
              {services.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name} - ${service.price}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Barber</InputLabel>
            <Select
              value={selectedBarber || ''}
              label="Barber"
              onChange={(e: SelectChangeEvent<number>) => 
                setSelectedBarber(e.target.value as number)
              }
            >
              {barbers.map((barber) => (
                <MenuItem key={barber.id} value={barber.id}>
                  {barber.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              disablePast
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={6}>
          <TimeSlots 
            availableSlots={availableSlots.map(slot => slot.time)}
            selectedTime={selectedTime}
            onTimeSelect={setSelectedTime}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!selectedService || !selectedBarber || !selectedDate || !selectedTime}
          >
            Book Appointment
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default BookAppointment; 